import { filter } from "rxjs/operators";
import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import type { MenuItem } from "primeng/api";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { AuthControlService, FLAT_SETTINGS, type SettingControlModel } from "@medsurf/flat-services";

@Component({
    selector: 'medsurf-navbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent {
  @Input() message: string;
  
  currentLanguage: string;
  versionNr: string;
  // availableLanguages = ['de', 'en'];
  helpMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.documentation'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DOCUMENTATION,
      routerLink: '/dokumentation',
    },
    {
      label: this.translate.instant('global.changelog'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.CHANGELOG,
      routerLink: '/changelog'
    },
  ];
  
  userMenuItems: MenuItem[] = [
    // add element after final implementation
    // {
    //   label: this.translate.instant('global.theme_switch'),
    //   icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.THEME,
    //   command: () => this.toggleTheme(),
    // },
    {
      label: this.translate.instant('global.user_profile'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.USER,
      command: () => this.authControlService.oidcAuthUserProfileRedirect()
    },
    {
      label: this.translate.instant('global.logout'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.LOGOUT,
      command: () => this.authControlService.oidcAuthLogout()
    }
  ]

  getMenuItemsAdministrationActions = () => {
    const currentUser = this.userSharedFacade.snapshot_currentUser();
    const isAdmin = currentUser?.role === FlatModels.UserEntityModels.Role.enum.admin
    return [
      {
        label: this.translate.instant('global.import_deepzoom'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.IMPORT_DEEPZOOM,
        routerLink: '/import-stats',
      },
      {
        label: this.translate.instant('global.detached_slides'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DETACHED_SLIDES,
        routerLink: '/detached',
      },
      {
        label: this.translate.instant('global.media_library'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA,
        routerLink: '/media',
      },
      {
        separator: true
      },
      {
        label: this.translate.instant('global.matomo_stats'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.MATOMO_STATS,
        routerLink: '/matomo-stats',
      },
      {
        separator: true,
        visible: isAdmin
      },
      {
        label: this.translate.instant('global.metadata'),
        visible: isAdmin,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.METADATA,
        routerLink: '/metadata',
      },
      {
        label: this.translate.instant('global.modalities'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.MODALITY,
        routerLink: '/modalities',
      },
      {
        label: this.translate.instant('global.stains'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.STAIN,
        routerLink: '/stains',
      },
      {
        label: this.translate.instant('global.copyrights'),
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.COPYRIGHT,
        routerLink: '/copyrights',
      },
      {
        separator: true,
        visible: isAdmin
      },
      {
        label: this.translate.instant('global.user_administration'),
        visible: isAdmin,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.USER,
        routerLink: '/users'
      }
    ];
  }
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
    private userSharedFacade: FlatFacades.UserSharedFacade,
    private alertControlFacade: FlatFacades.AlertControlFacade,
    private navigationControlFacade: FlatFacades.NavigationControlFacade,
    private authControlService: AuthControlService,
    private themeControlFacade: FlatFacades.ThemeControlFacade,
    private translate: TranslateService,
    @Inject(FLAT_SETTINGS) private settings: SettingControlModel) {
    const { version, timestamp, revision } = settings;
    this.versionNr = `${version} (${timestamp}), Revision: ${revision}`;
    this.#readCurrentLanguage();

    // Get Api Version
    this.appSharedFacade.apiVersion$.pipe(takeUntilDestroyed(), filter(apiVersion => !!apiVersion))
      .subscribe((apiVersion) => {
        if (!apiVersion || !this.settings.version) return;
        const [apiMajor, apiMinor] = apiVersion.split('.');
        const [authorMajor, authorMinor] = this.settings.version.split('.');
        if (apiMinor !== authorMinor || apiMajor !== authorMajor) {
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.version_mismatch',
            data: { apiVersion: [apiMajor, apiMinor].join('.'), authorVersion: [authorMajor, authorMinor].join('.') },
            severity: FlatModels.AlertControlModels.AlertType.enum.warn,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    this.appSharedFacade.requestGetApiVersion().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  openSearch() {
    this.navigationControlFacade.requestNavigate(['search'])
  }

  #readCurrentLanguage() {
    this.currentLanguage = this.translate.currentLang;
  }

  toggleTheme() {
    this.themeControlFacade.toggleTheme();
  }

  /*
  openContactModal() {
    (HS as any).beacon.open();
  }
  */

  /*
  changeLanguage(langKey: string) {
    this.translate.use(langKey).subscribe(() => {
      this.#readCurrentLanguage();
    });
  }
  */
}
